import React from "react";
import { useSearchParams } from "react-router-dom";
import * as _ from "underscore";
import ThemeProvider from "web-ui/components/ThemeProvider";
import Accordion from "web-ui/components/internal/Accordion";
import Button from "web-ui/components/internal/Button";
import Checkbox from "web-ui/components/internal/Checkbox";
import Dropdown from "web-ui/components/internal/Dropdown";
import Icon, { iconFactory } from "web-ui/components/internal/Icon";
import Loader from "web-ui/components/internal/Loader";
import Menu from "web-ui/components/internal/Menu";
import MenuItem from "web-ui/components/internal/MenuItem";
import Pagination from "web-ui/components/internal/Pagination";
import Select from "web-ui/components/internal/Select";
import TextInput from "web-ui/components/internal/TextInput";
import Toast from "web-ui/components/internal/Toast";
import useAwaitedValue from "web-ui/components/useAwaitedValue";
import usePopover, { bottomAlignRight } from "web-ui/components/usePopover";
import { COLORS_INTERNAL } from "web-ui/util/color";

import {
  type VoiceListVoice,
  getCombinedVoiceList,
  toVoiceListVoice,
} from "../../api/generalTts";
import type {
  LanguageItem,
  TtsGenerationAge,
  TtsGenerationGender,
  VoicesV2Item,
} from "../../api/ttsGenerationBackend";
import { getLanguages } from "../../api/ttsGenerationBackend";
import { getDisplayLanguageName } from "../../util/languageUtils";
import HrefLink from "../HrefLink";

import VoiceCard from "./VoiceCard";
import VoiceDrawer from "./VoiceDrawer";
import styles from "./VoiceFinder.module.scss";

const PER_PAGE = 13;

type Filter = (voice: VoiceListVoice) => boolean;

type SortField =
  | "added-at"
  | "added-at-reverse"
  | "language"
  | "language-reverse";

interface StructuredLanguageItem extends LanguageItem {
  dialects?: LanguageItem[];
}

export const formatCharacter = (character: string) =>
  titleCaseFormatter(character);

export const formatLanguage = (...languages: (string | null)[]) =>
  `${languages
    .slice(0, 2)
    .map(lang =>
      lang === null ? "N/A" : `${getDisplayLanguageName(lang)} (${lang})`,
    )
    .join(", ")}${languages.length > 2 ? ` +${languages.length - 2}` : ""}`;

export const formatProvider = (provider: string) => {
  switch (provider) {
    case "amazon":
      return "Amazon";
    case "azure":
      return "Azure";
    case "elevenlabs":
      return "ElevenLabs";
    case "google":
      return "Google";
    case "openai":
      return "OpenAI";
    case "recorded":
      return "Recorded";
  }
  return provider;
};

const matchLanguage = (voice: VoiceListVoice, bcp47: string) => {
  // https://docs.google.com/spreadsheets/d/13ehJ0lDkX5mVdNWOwCRGGDvSvHmQmzDGBfKaUB3dWZA/edit?gid=1393001542#gid=1393001542
  const matches = (() => {
    switch (bcp47) {
      case "arb":
        return ["ar", "ara", "arb", "ar-EG", "ar-SA", "ar-XA"];
      case "cmn-CN":
        return ["cmn-CN", "zh-CN", "zs"];
      case "tl-PH":
        return ["fil-PH", "tl-PH"];
      case "yue-HK":
        return ["yue-CN", "yue-HK", "zc", "zh-HK"];
    }
    return [bcp47];
  })();
  return voiceLanguages(voice).some(lang => matches.includes(lang));
};

export const titleCaseFormatter = (value: string) =>
  `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`;

const voiceLanguages = (voice: VoiceListVoice) => [
  ...(voice.primary_language === null ? [] : [voice.primary_language]),
  ...voice.language_statuses.map(status => status.language),
];

const CheckboxList = ({
  children,
  double,
}: {
  children: React.ReactNode;
  double?: boolean;
}) => (
  <div className={double ? styles["checkboxes-double"] : styles.checkboxes}>
    {children}
  </div>
);

const CheckboxOptions = ({
  double,
  format = value => value,
  onChange,
  options,
  value,
}: {
  double?: boolean;
  format?: (value: string) => string;
  onChange?: (newValue: string[]) => void;
  options: string[];
  value: string[];
}) => (
  <CheckboxList double={double}>
    {options.map(o => (
      <Checkbox
        checked={value.includes(o)}
        key={o}
        onChange={e => {
          if (e.checked && !value.includes(o)) {
            onChange?.([...value, o]);
          } else if (!e.checked && value.includes(o)) {
            onChange?.(value.filter(v => v !== o));
          }
        }}
      >
        {format(o)}
      </Checkbox>
    ))}
  </CheckboxList>
);

const SortMenu = ({
  onChange,
  value,
}: {
  onChange?: (newValue: SortField) => void;
  value: SortField;
}) => {
  const options: [value: SortField, displayText: string][] = [
    ["language", "Language (A to Z)"],
    ["language-reverse", "Language (Z to A)"],
    ["added-at-reverse", "Newest"],
    ["added-at", "Oldest"],
  ];

  const { anchorProps, anchorRef, clickTargetProps, popoverProps, popoverRef } =
    usePopover<HTMLButtonElement>({
      placement: bottomAlignRight(4),
    });

  return (
    <>
      <Button
        isGhost={true}
        isIconOnly={true}
        onBlur={clickTargetProps.onBlur}
        ref={anchorRef}
        variant="secondary"
        {...anchorProps}
      >
        <Icon name="sort" />
      </Button>
      <Dropdown
        className={styles.sort}
        onBlur={clickTargetProps.onBlur}
        onMouseDown={clickTargetProps.onMouseDown}
        ref={popoverRef}
        type="menu"
        {...popoverProps}
      >
        <Menu>
          {options.map(([v, t]) => (
            <MenuItem
              key={v}
              onChange={e => e.selected && onChange?.(v)}
              state={v === value ? "selected" : "enabled"}
            >
              {t}
            </MenuItem>
          ))}
        </Menu>
      </Dropdown>
    </>
  );
};

const VoiceFinder = () => {
  const [accordionKey, rerenderAccordion] = React.useReducer(
    value => value + 1,
    0,
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const filterAges = searchParams.getAll("age");
  const filterCharacters = searchParams.getAll("character");
  const filterCollection = searchParams.get("collection") ?? "";
  const filterGenders = searchParams.getAll("gender");
  const filterIsAvailable = searchParams.get("is-available") === "true";
  const filterIsExpressive = searchParams.get("is-expressive") === "true";
  const filterIsMultilingual = searchParams.get("is-multilingual") === "true";
  const filterIsUnavailable = searchParams.get("is-unavailable") === "true";
  const filterLanguages = searchParams.getAll("language");
  const filterMissingTags = searchParams.get("missing-tags") === "true";
  const filterProviders = searchParams.getAll("provider");
  const filterTargetLanguages =
    searchParams.get("target-languages") !== "false";
  const filterUiLanguages = searchParams.get("ui-languages") === "true";
  const page = parseInt(searchParams.get("page") ?? "1", 10);
  const query = searchParams.get("query") ?? "";
  const selectedId = searchParams.get("id") ?? undefined;
  const sortBy = (searchParams.get("sort-by") ?? "language") as SortField;

  const [localChanges, writeLocalChange] = React.useReducer(
    (prevState: Record<string, VoiceListVoice>, result: VoicesV2Item) => ({
      ...prevState,
      [result.id]: toVoiceListVoice(result),
    }),
    {},
  );

  const [showDrawer, setShowDrawer] = React.useState(false);
  const [showToast, setShowToast] = React.useState(false);
  const [toast, setToast] = React.useState<string>();

  const backendData = useAwaitedValue(
    undefined,
    async () =>
      Object.fromEntries(
        (await getCombinedVoiceList()).map(voice => [voice.id, voice]),
      ),
    [],
  );
  const languageData = useAwaitedValue(undefined, () => getLanguages(), []);

  const data = React.useMemo(
    () =>
      Object.values({
        ...backendData,
        ...localChanges,
      }),
    [backendData, localChanges],
  );

  const languageDataByBcp47 = React.useMemo(
    () =>
      languageData === undefined
        ? undefined
        : Object.fromEntries(languageData.map(lang => [lang.bcp47, lang])),
    [languageData],
  );

  const selected = React.useMemo(
    () => data?.find(voice => voice.id === selectedId),
    [data, selectedId],
  );

  const ageOptions = React.useMemo(
    () =>
      _.uniq(data?.map(voice => voice.age) ?? [])
        .filter((o): o is TtsGenerationAge => o !== null && o !== undefined)
        .sort((a, b) =>
          titleCaseFormatter(a).localeCompare(titleCaseFormatter(b)),
        ),
    [data],
  );

  const characterOptions = React.useMemo(
    () =>
      _.uniq(
        data?.flatMap(voice =>
          voice.world_character === null || voice.world_character === undefined
            ? []
            : [voice.world_character],
        ) ?? [],
      ).sort((a, b) => formatCharacter(a).localeCompare(formatCharacter(b))),
    [data],
  );

  const collections = React.useMemo(
    () =>
      _.uniq(data?.flatMap(voice => voice.collections) ?? []).sort((a, b) =>
        a.localeCompare(b),
      ),
    [data],
  );

  const genderOptions = React.useMemo(
    () =>
      _.uniq(data?.map(voice => voice.gender) ?? [])
        .filter((o): o is TtsGenerationGender => o !== null && o !== undefined)
        .sort((a, b) =>
          titleCaseFormatter(a).localeCompare(titleCaseFormatter(b)),
        ),
    [data],
  );

  const languageOptions = React.useMemo(
    () =>
      languageData?.filter(
        lang =>
          (!filterTargetLanguages || lang.is_target_lang) &&
          (!filterUiLanguages || lang.is_ui_lang),
      ) ?? [],
    [filterTargetLanguages, filterUiLanguages, languageData],
  );

  const languageOptionsStructured =
    React.useMemo((): StructuredLanguageItem[] => {
      const groups = _.groupBy(
        languageOptions,
        lang => lang.language_group ?? lang.name,
      );
      return Object.values(groups)
        .map(group => {
          if (group.length === 1) {
            return group[0];
          }
          const defaultDialect =
            group.find(lang => lang.default_dialect) ?? group[0];
          return {
            ...defaultDialect,
            dialects: _.sortBy(group, "bcp47"),
            name: defaultDialect.language_group ?? defaultDialect.name,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    }, [languageOptions]);

  const providerOptions = React.useMemo(
    () =>
      _.uniq(data?.map(voice => voice.provider) ?? []).sort((a, b) =>
        formatProvider(a).localeCompare(formatProvider(b)),
      ),
    [data],
  );

  const [filters, filterCount] = React.useMemo(() => {
    const newFilters: Filter[] = [];
    let newCount = 0;
    if (filterAges.length) {
      newFilters.push(voice => filterAges.includes(voice.age ?? ""));
      newCount += filterAges.length;
    }
    if (filterCharacters.length) {
      newFilters.push(voice =>
        filterCharacters.includes(voice.world_character ?? ""),
      );
      newCount += filterCharacters.length;
    }
    if (filterCollection.length) {
      newFilters.push(voice => voice.collections.includes(filterCollection));
      newCount++;
    }
    if (filterGenders.length) {
      newFilters.push(voice => filterGenders.includes(voice.gender ?? ""));
      newCount += filterGenders.length;
    }
    if (filterIsAvailable) {
      newFilters.push(voice => voice.status !== "unavailable");
      newCount++;
    }
    if (filterIsExpressive) {
      newFilters.push(voice => !!voice.is_expressive);
      newCount++;
    }
    if (filterIsMultilingual) {
      newFilters.push(voice => !!voice.is_multilingual);
      newCount++;
    }
    if (filterIsUnavailable) {
      newFilters.push(voice => voice.status === "unavailable");
      newCount++;
    }
    if (filterLanguages.length) {
      newFilters.push(voice =>
        filterLanguages.some(lang => matchLanguage(voice, lang)),
      );
      newCount += filterLanguages.length;
    }
    if (filterMissingTags) {
      newFilters.push(
        voice =>
          voice.age === null ||
          voice.gender === null ||
          voice.world_character === null,
      );
      newCount++;
    }
    if (filterProviders.length) {
      newFilters.push(voice => filterProviders.includes(voice.provider));
      newCount += filterProviders.length;
    }
    if (filterTargetLanguages || filterUiLanguages) {
      newFilters.push(voice =>
        voiceLanguages(voice).some(
          lang =>
            (filterTargetLanguages &&
              languageDataByBcp47?.[lang]?.is_target_lang) ||
            (filterUiLanguages && languageDataByBcp47?.[lang]?.is_ui_lang),
        ),
      );
      if (filterTargetLanguages) {
        newCount++;
      }
      if (filterUiLanguages) {
        newCount++;
      }
    }
    return [newFilters, newCount];
  }, [
    filterAges,
    filterCharacters,
    filterCollection,
    filterGenders,
    filterIsAvailable,
    filterIsExpressive,
    filterIsMultilingual,
    filterIsUnavailable,
    filterLanguages,
    filterMissingTags,
    filterProviders,
    filterTargetLanguages,
    filterUiLanguages,
    languageDataByBcp47,
  ]);

  const filteredData = React.useMemo(
    () =>
      data
        ?.filter(
          voice =>
            filters.every(fn => fn(voice)) &&
            (query === "" ||
              voice.name.toLowerCase().includes(query.toLowerCase())),
        )
        .sort((a, b) => {
          if (sortBy === "language") {
            return formatLanguage(a.primary_language).localeCompare(
              formatLanguage(b.primary_language),
            );
          } else if (sortBy === "language-reverse") {
            return formatLanguage(b.primary_language).localeCompare(
              formatLanguage(a.primary_language),
            );
          } else if (sortBy === "added-at") {
            return (a.addedAt?.valueOf() ?? 0) - (b.addedAt?.valueOf() ?? 0);
          } else if (sortBy === "added-at-reverse") {
            return (b.addedAt?.valueOf() ?? 0) - (a.addedAt?.valueOf() ?? 0);
          }
          return 0;
        }),
    [data, filters, query, sortBy],
  );

  const handleArrayFilter = (name: string, newValue: string[]) => {
    setSearchParams(params => {
      params.delete(name);
      params.delete("page");
      newValue.forEach(item => params.append(name, item));
      return params;
    });
  };

  const handleBooleanFilter = (
    name: string,
    newValue: boolean,
    inverse = false,
  ) => {
    setSearchParams(params => {
      if (newValue) {
        if (inverse) {
          params.delete(name);
        } else {
          params.set(name, "true");
        }
      } else {
        if (inverse) {
          params.set(name, "false");
        } else {
          params.delete(name);
        }
      }
      params.delete("page");
      return params;
    });
  };

  const handleStringFilter = (name: string, newValue: string) => {
    setSearchParams(params => {
      if (newValue) {
        params.set(name, newValue);
      } else {
        params.delete(name);
      }
      params.delete("page");
      return params;
    });
  };

  const handleReset = () => {
    setSearchParams(params => {
      params.delete("age");
      params.delete("character");
      params.delete("gender");
      params.delete("is-available");
      params.delete("is-expressive");
      params.delete("is-multilingual");
      params.delete("is-unavailable");
      params.delete("language");
      params.delete("missing-tags");
      params.delete("page");
      params.delete("provider");
      params.delete("query");
      params.delete("target-languages");
      params.delete("ui-languages");
      return params;
    });
    // Resets the expanded panels.
    rerenderAccordion();
  };

  React.useEffect(() => {
    setShowDrawer(selected !== undefined);
  }, [selected]);

  return (
    <div className={styles.container}>
      <div className={styles.sidebar}>
        <TextInput
          onChange={e => handleStringFilter("query", e.value)}
          placeholder="Search by voice name"
          trailingVisual={iconFactory("search")}
          type="search"
          value={query}
        />
        <div className={styles.menu}>
          <span className={styles["menu-name"]}>Collections</span>
          <Menu>
            <MenuItem
              onChange={e => e.selected && handleStringFilter("collection", "")}
              state={filterCollection === "" ? "selected" : "enabled"}
            >
              All
            </MenuItem>
            {collections.map(name => (
              <MenuItem
                key={name}
                onChange={e =>
                  e.selected && handleStringFilter("collection", name)
                }
                state={filterCollection === name ? "selected" : "enabled"}
              >
                {name}
              </MenuItem>
            ))}
          </Menu>
        </div>
        <div className={styles.menu2}>
          <span className={styles["menu-header"]}>
            <span className={styles["menu-name"]}>Filters</span>
            <span className={styles.count}>{filterCount}</span>
          </span>
          <Accordion
            bordered="in-container"
            inset={false}
            items={[
              {
                content: (
                  <>
                    <Select
                      className={styles.select}
                      displayText={
                        languageDataByBcp47 === undefined
                          ? ""
                          : filterLanguages
                              .map(bcp47 => languageDataByBcp47[bcp47]?.name)
                              .join(", ")
                      }
                      dropdownProps={{
                        style: {
                          position: "fixed",
                        },
                      }}
                      placeholder="Select language(s)"
                    >
                      <Menu multiple={true}>
                        {languageOptionsStructured.map(item => (
                          <MenuItem
                            key={item.name}
                            onChange={e =>
                              handleArrayFilter(
                                "language",
                                e.selected
                                  ? _.uniq([...filterLanguages, item.bcp47])
                                  : _.without(filterLanguages, item.bcp47),
                              )
                            }
                            state={
                              filterLanguages.includes(item.bcp47)
                                ? "selected"
                                : "enabled"
                            }
                            submenu={
                              item.dialects?.length ? (
                                <Menu multiple={true}>
                                  {item.dialects.map(dialect => (
                                    <MenuItem
                                      key={dialect.bcp47}
                                      onChange={e =>
                                        handleArrayFilter(
                                          "language",
                                          e.selected
                                            ? _.uniq([
                                                ...filterLanguages,
                                                dialect.bcp47,
                                              ])
                                            : _.without(
                                                filterLanguages,
                                                dialect.bcp47,
                                              ),
                                        )
                                      }
                                      state={
                                        filterLanguages.includes(dialect.bcp47)
                                          ? "selected"
                                          : "enabled"
                                      }
                                    >
                                      {dialect.language_group} ({dialect.bcp47})
                                    </MenuItem>
                                  ))}
                                </Menu>
                              ) : undefined
                            }
                            submenuDropdownProps={{
                              style: {
                                position: "fixed",
                              },
                            }}
                          >
                            {item.name}
                          </MenuItem>
                        ))}
                      </Menu>
                    </Select>
                    <CheckboxList>
                      <Checkbox
                        checked={filterTargetLanguages}
                        onChange={e =>
                          handleBooleanFilter(
                            "target-languages",
                            e.checked,
                            true,
                          )
                        }
                      >
                        Target languages
                      </Checkbox>
                      <Checkbox
                        checked={filterUiLanguages}
                        onChange={e =>
                          handleBooleanFilter("ui-languages", e.checked)
                        }
                      >
                        UI languages
                      </Checkbox>
                    </CheckboxList>
                  </>
                ),
                defaultExpanded: true,
                header: "Language",
              },
              {
                content: (
                  <CheckboxOptions
                    double={true}
                    format={formatCharacter}
                    onChange={newValue =>
                      handleArrayFilter("character", newValue)
                    }
                    options={characterOptions}
                    value={filterCharacters}
                  />
                ),
                header: "Character",
              },
              {
                content: (
                  <CheckboxOptions
                    format={titleCaseFormatter}
                    onChange={newValue => handleArrayFilter("gender", newValue)}
                    options={genderOptions}
                    value={filterGenders}
                  />
                ),
                header: "Gender",
              },
              {
                content: (
                  <CheckboxOptions
                    double={true}
                    format={titleCaseFormatter}
                    onChange={newValue => handleArrayFilter("age", newValue)}
                    options={ageOptions}
                    value={filterAges}
                  />
                ),
                header: "Age",
              },
              {
                content: (
                  <CheckboxList>
                    <Checkbox
                      checked={filterIsMultilingual}
                      onChange={e =>
                        handleBooleanFilter("is-multilingual", e.checked)
                      }
                    >
                      Multilingual
                    </Checkbox>
                    <Checkbox
                      checked={filterIsExpressive}
                      onChange={e =>
                        handleBooleanFilter("is-expressive", e.checked)
                      }
                    >
                      Expressive
                    </Checkbox>
                  </CheckboxList>
                ),
                header: "Voice features",
              },
              {
                content: (
                  <CheckboxOptions
                    format={formatProvider}
                    onChange={newValue =>
                      handleArrayFilter("provider", newValue)
                    }
                    options={providerOptions}
                    value={filterProviders}
                  />
                ),
                header: "Provider",
              },
              {
                content: <>This section hasn&apos;t been implemented yet</>,
                header: "Quality",
              },
              {
                content: (
                  <CheckboxList>
                    <Checkbox
                      checked={filterIsAvailable}
                      onChange={e =>
                        handleBooleanFilter("is-available", e.checked)
                      }
                    >
                      Available
                    </Checkbox>
                    <Checkbox
                      checked={filterIsUnavailable}
                      onChange={e =>
                        handleBooleanFilter("is-unavailable", e.checked)
                      }
                    >
                      Unavailable
                    </Checkbox>
                  </CheckboxList>
                ),
                header: "Status",
              },
              {
                content: (
                  <CheckboxList>
                    <Checkbox
                      checked={filterMissingTags}
                      onChange={e =>
                        handleBooleanFilter("missing-tags", e.checked)
                      }
                    >
                      Missing tags
                    </Checkbox>
                  </CheckboxList>
                ),
                header: "Tags",
              },
            ]}
            key={accordionKey}
            textStyle="uppercase"
          />
          <Button
            className={styles.reset}
            onClick={handleReset}
            size="s"
            variant="secondary"
          >
            Reset
          </Button>
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.h1}>{filterCollection || "All"} Voices</h1>
          <div className={styles["header-total"]}>
            {data === undefined || filteredData === undefined
              ? ""
              : filteredData.length === data.length
                ? `${filteredData.length} ${filteredData.length === 1 ? "voice" : "voices"}`
                : `${filteredData.length} of ${data.length} voices`}
          </div>
          <div className={styles["header-buttons"]}>
            <SortMenu
              onChange={newValue => handleStringFilter("sort-by", newValue)}
              value={sortBy}
            />
            <Button
              leadingVisual={<Icon color="gray-600" name="refresh" />}
              variant="secondary"
            >
              Sync
            </Button>
          </div>
        </div>
        <div className={styles.list}>
          {filteredData === undefined ? (
            <Loader className={styles.loader} />
          ) : filteredData.length === 0 ? (
            <span className={styles.empty}>
              No voices match the filter selection.
            </span>
          ) : (
            filteredData
              .slice((page - 1) * PER_PAGE, page * PER_PAGE)
              .map(voice => (
                <VoiceCard
                  key={voice.name}
                  onSelect={() => {
                    setSearchParams(params => {
                      params.set("id", voice.id);
                      return params;
                    });
                    setShowDrawer(true);
                  }}
                  state={voice === selected ? "selected" : "enabled"}
                  voice={voice}
                />
              ))
          )}
        </div>
        {filteredData?.length ? (
          <div className={styles.footer}>
            <Pagination
              className={styles.pagination}
              formatLink={newPage => {
                const params = new URLSearchParams(searchParams);
                params.set("page", `${newPage}`);
                return `?${params.toString()}`;
              }}
              linkComponent={HrefLink}
              page={page}
              totalPages={Math.ceil(filteredData.length / PER_PAGE)}
            />
          </div>
        ) : null}
      </div>
      <VoiceDrawer
        className={styles.drawer}
        exampleSentence={
          selected?.primary_language
            ? languageDataByBcp47?.[selected.primary_language]?.example_sentence
            : undefined
        }
        onChange={(type, updatedVoice) => {
          writeLocalChange(updatedVoice);
          if (type === "clone") {
            setSearchParams(params => {
              params.set("id", updatedVoice.id);
              return params;
            });
            setShowToast(true);
            setToast("Voice successfully cloned");
          } else {
            setShowToast(true);
            setToast("Edits saved successfully");
          }
        }}
        onRequestClose={() => setShowDrawer(false)}
        open={showDrawer}
        voice={selected}
      />
      <ThemeProvider colors={COLORS_INTERNAL} theme="dark">
        {({ style }) => (
          <Toast
            isVisible={showToast}
            onRequestClose={() => setShowToast(false)}
            placement="bottom-left"
            style={style}
          >
            {toast}
          </Toast>
        )}
      </ThemeProvider>
    </div>
  );
};

export default VoiceFinder;
